/**
 * React Query hook for Users (Global)
 * Provides automatic caching, background sync, and error handling
 * Can be used across all modules
 */

import { useQuery } from '@tanstack/react-query';
import { listUsers } from 'services/teamManagement.service';
import { totalCountEvent } from 'services/generalServices';
import { queryKeys } from '../config/queryClient';

/**
 * Fetches users data
 * Always loads the full list by getting total count first, then fetching all items
 * Same as CreateExpense implementation
 *
 * @param {string} status - User status filter (default: 'ACTIVE')
 * @param {number} pageLimit - Limit for fetching users
 * @param {string} pagination_token - Pagination token
 * @param {string} pagination_direction - Pagination direction ('forward' or 'reverse')
 * @param {string} keyword - Search keyword
 * @param {string} next - Next page token
 * @param {boolean} is_include_contacts - Include contacts (default: false)
 * @param {boolean} is_detailed - Include detailed info (default: false)
 * @param {boolean} is_confirmed_user - Filter confirmed users (default: true)
 * @param {boolean} is_include_groups - Include groups (default: false)
 * @param {boolean} active_groups - Include active groups only (default: false)
 * @param {boolean} excludeFromLoading - Exclude from loading state (default: false)
 * @returns {Promise<Array>} - Array of users (firm_users)
 */
const fetchUsers = async (
  status = 'ACTIVE',
  pageLimit,
  pagination_token = '',
  pagination_direction = 'forward',
  keyword = '',
  next = '',
  is_include_contacts = false,
  is_detailed = false,
  is_confirmed_user = true,
  is_include_groups = false,
  active_groups = false,
  excludeFromLoading = false
) => {
  try {
    // Fetch users with all parameters
    const response = await listUsers(
      status,
      pageLimit,
      pagination_token,
      pagination_direction,
      keyword,
      is_include_contacts,
      is_detailed,
      is_confirmed_user,
      is_include_groups,
      false, // active_case_count
      active_groups,
      excludeFromLoading
    );

    return response?.data?.firm_users || [];
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

/**
 * Hook to fetch users with React Query
 * Always loads the full list by getting total count first (same as CreateExpense implementation)
 *
 * @param {Object} options - Configuration options
 * @param {string} options.status - User status filter (default: 'ACTIVE')
 * @param {number} options.limit - Number of items per page (default: uses total count)
 * @param {string} options.pagination_token - Pagination token (default: '')
 * @param {string} options.pagination_direction - Pagination direction (default: 'forward')
 * @param {string} options.keyword - Search keyword (default: '')
 * @param {string} options.next - Next page token (default: '')
 * @param {boolean} options.is_include_contacts - Include contacts (default: false)
 * @param {boolean} options.is_detailed - Include detailed info (default: false)
 * @param {boolean} options.is_confirmed_user - Filter confirmed users (default: true)
 * @param {boolean} options.is_include_groups - Include groups (default: false)
 * @param {boolean} options.active_groups - Include active groups only (default: false)
 * @param {boolean} options.excludeFromLoading - Exclude from loading state (default: false)
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 *
 * @returns {Object} - userList, isUserLoading, error, refetch, isFetching
 */
export const useUsersQuery = ({
  status = 'ACTIVE',
  limit,
  pagination_token = '',
  pagination_direction = 'forward',
  keyword = '',
  next = '',
  is_include_contacts = false,
  is_detailed = false,
  is_confirmed_user = true,
  is_include_groups = false,
  active_groups = false,
  excludeFromLoading = false,
  enabled = true,
} = {}) => {
  // Get total count first (same as CreateExpense) - only if limit is not provided
  const { data: countData } = useQuery({
    queryKey: ['user-count', status],
    queryFn: async () => {
      const countResponse = await totalCountEvent('user');
      return countResponse?.data || 10;
    },
    enabled: enabled && !limit, // Only fetch count if limit is not provided
    staleTime: 10 * 60 * 1000, // Consider data stale after 10 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes (gcTime replaces cacheTime in v4+)
    refetchOnWindowFocus: false, // Don't refetch on window focus for relatively static data
    retry: 2, // Retry twice on failure
  });

  const pageLimit = limit || countData || 10;

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: queryKeys.users.list(
      status,
      pageLimit,
      pagination_token,
      pagination_direction,
      keyword,
      next,
      is_include_contacts,
      is_detailed,
      is_confirmed_user,
      is_include_groups,
      active_groups
    ),
    queryFn: () =>
      fetchUsers(
        status,
        pageLimit,
        pagination_token,
        pagination_direction,
        keyword,
        next,
        is_include_contacts,
        is_detailed,
        is_confirmed_user,
        is_include_groups,
        active_groups,
        excludeFromLoading
      ),
    enabled: enabled && (limit || !!countData), // Only fetch when limit is provided or count is available
    staleTime: 10 * 60 * 1000, // Consider data stale after 10 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes (gcTime replaces cacheTime in v4+)
    refetchOnWindowFocus: false, // Don't refetch on window focus for relatively static data
    retry: 2, // Retry twice on failure
  });

  return {
    userList: data || [],
    isUserLoading: isLoading,
    isFetching,
    error,
    refetch,
  };
};
